import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  HelpOutline as HelpIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../config/api";
import { useOrganization } from "../contexts/OrganizationContext";

const MONTSERRAT_DEFAULT = "'Montserrat', sans-serif";

interface FaqItem {
  id: string;
  question: string;
  answer: string;
  category: string;
  order: number;
}

const CATEGORY_COLORS: Record<string, string> = {
  General: "#1976d2",
  "League Days": "#388e3c",
  "Points & Rankings": "#f57c00",
  Divisions: "#7b1fa2",
  Playing: "#0097a7",
  Registration: "#c62828",
  Rules: "#37474f",
  Schedule: "#00838f",
  Fees: "#558b2f",
};

const PE_FAQ_COLORS = ["#743444", "#E49421", "#74586C", "#B79498"];
function colorForCategory(cat: string, isPE = false): string {
  if (isPE) {
    let h = 0;
    for (const ch of cat) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    return PE_FAQ_COLORS[h % PE_FAQ_COLORS.length];
  }
  return CATEGORY_COLORS[cat] ?? "#1976d2";
}

const PublicFaq: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrg } = useOrganization();
  const MONTSERRAT = currentOrg?.slug === "padel-eagle" ? "'Bai Jamjuree', sans-serif" : MONTSERRAT_DEFAULT;
  const [items, setItems] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await apiRequest("/api/faq");
        const data = await res.json();
        setItems(Array.isArray(data) ? data : []);
      } catch {
        setError("Could not load FAQ. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const categories = useMemo(
    () => Array.from(new Set(items.map((i) => i.category).filter(Boolean))),
    [items],
  );

  const filtered = activeCategory
    ? items.filter((i) => i.category === activeCategory)
    : items;

  const orgName = currentOrg?.name ?? "our league";

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, md: 3 } }}>
      {/* Back button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/")}
        sx={{
          mb: 3,
          fontFamily: MONTSERRAT,
          fontWeight: 600,
          textTransform: "none",
          color: "text.secondary",
          "&:hover": { color: "text.primary" },
        }}
      >
        Back to homepage
      </Button>

      {/* Header */}
      <Box sx={{ textAlign: "center", mb: { xs: 3, md: 5 } }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: "primary.main",
            mb: 2,
          }}
        >
          <HelpIcon sx={{ color: "white", fontSize: 32 }} />
        </Box>
        <Typography
          variant="h3"
          sx={{
            fontFamily: MONTSERRAT,
            fontWeight: 900,
            fontSize: { xs: "1.8rem", md: "2.5rem" },
            mb: 1,
          }}
        >
          Frequently Asked Questions
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontFamily: MONTSERRAT, maxWidth: 520, mx: "auto" }}
        >
          Everything you need to know about {orgName}.
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && items.length === 0 && (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <HelpIcon sx={{ fontSize: 56, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ fontFamily: MONTSERRAT }}>
            No FAQ items have been added yet.
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ fontFamily: MONTSERRAT, mt: 1 }}>
            Check back later or contact the league organizers directly.
          </Typography>
        </Box>
      )}

      {!loading && !error && items.length > 0 && (
        <>
          {/* Category filter */}
          {categories.length > 1 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                justifyContent: "center",
                mb: 3,
              }}
            >
              <Chip
                label="All"
                onClick={() => setActiveCategory(null)}
                variant={activeCategory === null ? "filled" : "outlined"}
                sx={{
                  fontFamily: MONTSERRAT,
                  fontWeight: 600,
                  ...(activeCategory === null && {
                    bgcolor: "primary.main",
                    color: "white",
                    "&:hover": { bgcolor: "primary.dark" },
                  }),
                }}
              />
              {categories.map((cat) => (
                <Chip
                  key={cat}
                  label={cat}
                  onClick={() =>
                    setActiveCategory(activeCategory === cat ? null : cat)
                  }
                  variant={activeCategory === cat ? "filled" : "outlined"}
                  sx={{
                    fontFamily: MONTSERRAT,
                    fontWeight: 600,
                    ...(activeCategory === cat && {
                      bgcolor: colorForCategory(cat, currentOrg?.slug === "padel-eagle"),
                      color: "white",
                      "&:hover": {
                        bgcolor: colorForCategory(cat, currentOrg?.slug === "padel-eagle"),
                        filter: "brightness(0.9)",
                      },
                    }),
                  }}
                />
              ))}
            </Box>
          )}

          {/* Accordions */}
          <Box>
            {filtered.map((item) => (
              <Accordion
                key={item.id}
                disableGutters
                elevation={0}
                sx={{
                  mb: 1,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "12px !important",
                  "&:before": { display: "none" },
                  "&.Mui-expanded": {
                    boxShadow: (theme) =>
                      theme.palette.mode === "dark"
                        ? "0 4px 20px rgba(0,0,0,0.4)"
                        : "0 4px 20px rgba(0,0,0,0.07)",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    borderRadius: "12px",
                    px: { xs: 2, md: 2.5 },
                    "& .MuiAccordionSummary-content": {
                      my: 1.5,
                      gap: 1.5,
                      alignItems: "center",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 20,
                      borderRadius: 2,
                      flexShrink: 0,
                      bgcolor: colorForCategory(item.category, currentOrg?.slug === "padel-eagle"),
                    }}
                  />
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontFamily: MONTSERRAT,
                      fontSize: { xs: "0.9rem", md: "1rem" },
                    }}
                  >
                    {item.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 2.5 }, pt: 0, pb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontFamily: MONTSERRAT,
                      lineHeight: 1.75,
                      fontSize: { xs: "0.875rem", md: "0.95rem" },
                      pl: "19px",
                    }}
                  >
                    {item.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </>
      )}

      {/* Footer */}
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontFamily: MONTSERRAT, mb: 1.5 }}
        >
          Still have questions?
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate("/")}
          sx={{
            fontFamily: MONTSERRAT,
            fontWeight: 700,
            textTransform: "none",
            borderRadius: 2,
            px: 3,
          }}
        >
          Back to the homepage
        </Button>
      </Box>
    </Container>
  );
};

export default PublicFaq;
